/**
 * Email validation utilities
 */

// RFC 5322 simplified email validation regex
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Validates a single email address
 * @param email - Email address to validate
 * @returns true if email is valid, false otherwise
 */
export function validateEmail(email: string): boolean {
  return EMAIL_REGEX.test(email.trim());
}

/**
 * Parses a comma-separated list of email addresses
 * @param input - Comma-separated string of emails
 * @returns Array of trimmed email addresses
 */
export function parseEmailList(input: string): string[] {
  return input
    .split(',')
    .map((email) => email.trim())
    .filter((email) => email.length > 0);
}

/**
 * Removes duplicate email addresses (case-insensitive)
 * @param emails - Array of email addresses
 * @returns Array of unique email addresses
 */
export function deduplicateEmails(emails: string[]): string[] {
  const seen = new Set<string>();
  return emails.filter((email) => {
    const normalized = email.toLowerCase();
    if (seen.has(normalized)) {
      return false;
    }
    seen.add(normalized);
    return true;
  });
}

/**
 * Validates an array of email addresses and returns validation errors
 * @param emails - Array of email addresses to validate
 * @returns Object with isValid flag and array of invalid emails
 */
export function validateEmailList(emails: string[]): {
  isValid: boolean;
  invalidEmails: string[];
} {
  const invalidEmails = emails.filter((email) => !validateEmail(email));
  return {
    isValid: invalidEmails.length === 0,
    invalidEmails,
  };
}
